<template>
  <div id="FamilyBadgeManagement">
    <v-form ref="familyBadgeManagementForm">
      <v-container class="pa-0">
        <v-layout>
          <v-flex class="xs6 sm6 mb-5">
            <v-btn title="Add a badge" fab absolute right class="mr-3 pf-btn-fab" @click="addBadge">
              <v-icon>add</v-icon>
            </v-btn>
            <v-btn title="Remove a badge" v-if="badgeOptions.length > 1" fab absolute right class="pf-btn-remove pf-btn-fab" @click="removeBadge">
              <v-icon>remove</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container>
        <v-layout class="button-container pr-4 pl-4"> 
          <v-flex xs12 sm12 md12 v-if="isFormDataChanged" class="pr-0 text-xs-right">
            <div class="d-inline-flex">
              <v-btn class="pf-btn pf-btn-cancel" @click="hideButtons">
                <v-icon size="16" class="pr-1">cancel</v-icon>
                <div>Cancel</div>
              </v-btn>
              <v-btn class="pf-btn pf-btn-save" @click="save">
                <v-icon size="16" class="pr-1">check_circle</v-icon>
                <div>Save</div>
              </v-btn>
            </div>
          </v-flex>
        </v-layout>
        <v-container v-for="(badgeOption, i) in badgeOptions" :key="i" :id="`badge${i+1}`">
          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12" v-if="badgeOptions.length > 1">
              <h3 class="pf-head mb-4 custom-title">
                Badge {{ (i + 1) }}
              </h3>
            </v-flex>
          </v-layout>
          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>1. Display Options:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.numberOfFamilies" label="Number of families to display" :items="noOfFamilies" class="pr-4" @change="isFormDataChanged = true">
              </v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.colsPerRow" label="Profiles per row" hint="Number of families listed in a single row" :items="noOfCols" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6 v-if="badgeOption.generalOptions.filteringOptions.indexOf('sort_by') > -1">
              <v-select v-model="badgeOption.generalOptions.sortingOptions" label="Sorting options" :items="sortingOptions" class="pr-4" small-chips multiple deletable-chips @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6 v-else>
              <v-select v-model="badgeOption.generalOptions.sortingOptions[0]" label="Sorting options" :items="sortingOptions" class="pr-4" small-chips deletable-chips @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.filteringOptions" label="Filtering options" :items="filteringOptions" class="pr-4" multiple small-chips deletable-chips @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.disableInprogressProfiles" label="Disable in progress profiles" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.kidsInFamilyOption" label="Kids in family option" :items="[{ text: 'Parentfinder', value: 0 }, { text: 'Badge', value: 1 }]" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.showCountryState" label="Show country/state" :items="[{ text: 'Country', value: 'country'}, { text: 'State', value: 'state' }, { text: 'Both', value: 'both' }]" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.fontFamily" label="Font family" :items="[ { text: 'From current theme', value: 'theme' }, { text: 'From plugin', value: 'plugin' }]" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.generalOptions.showVideo" label="Show agency video" hint="Your video will be showed in the badge when the family doesn't have videos" persistent-hint :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>2. Protect Profiles:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <small>* Applicabe only if the family account was set as private</small>
              <v-text-field
              @input="isFormDataChanged = true"
              v-model="badgeOption.generalOptions.profilePassword"
              :append-icon="showPassword ? 'visibility' : 'visibility_off'"
              @click:append="showPassword = !showPassword"
              :type="showPassword ? 'text' : 'password'"
              label="Profile password" class="pr-4"></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>3. Default Sorting:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <v-select v-model="badgeOption.generalOptions.defaultSorting.item" label="Default sorting item" :items="getDefaultSortingItems(badgeOption)" hint="To list families first in the badge" persistent-hint class="pr-4" @change="getDefaultSortingOptions(badgeOption)"></v-select>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4" v-if="badgeOption.generalOptions.defaultSorting.item !== 'None'">
              <v-select v-model="badgeOption.generalOptions.defaultSorting.value" label="Default sorting option" hint="Matched families will be listed first in the badge" :items="defaultSortingOptions" persistent-hint class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>4. Customization:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head mb-3 custom-title">Primary color</span>
              <v-btn block :color="badgeOption.color.primary" @click="pickPrimaryColor = !pickPrimaryColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.primary" @input="changePrimaryColor" v-if="pickPrimaryColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head mb-3 custom-title">Secondary color</span>
              <v-btn block :color="badgeOption.color.secondary" @click="pickSecondaryColor = !pickSecondaryColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.secondary" @input="changeSecondaryColor" v-if="pickSecondaryColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head mb-3 custom-title">Top bar color</span>
              <v-btn block :color="badgeOption.color.topbar" @click="pickTopbarColor = !pickTopbarColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.topbar" @input="changeTopbarColor" v-if="pickTopbarColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head mb-3 custom-title">Top bar text color</span>
              <v-btn block :color="badgeOption.color.topbarText" @click="pickTopbarTextColor = !pickTopbarTextColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.topbarText" @input="changeTopbarTextColor" v-if="pickTopbarTextColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head custom-title">Matched text</span>
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.badgeText.matched" label="Matched badge text" class="mt-2"></v-text-field>
              <v-btn block :color="badgeOption.color.matched" @click="pickMatchedColor = !pickMatchedColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.matched" @input="changeMatchedColor" v-if="pickMatchedColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head custom-title">Placed text</span>
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.badgeText.placed" label="Placed badge text" class="mt-2"></v-text-field>
              <v-btn block :color="badgeOption.color.placed" @click="pickPlacedColor = !pickPlacedColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.placed" @input="changePlacedColor" v-if="pickPlacedColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head custom-title">In progress text</span>
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.badgeText['in progress']" label="In progress badge text" class="mt-2"></v-text-field>
              <v-btn block :color="badgeOption.color['in progress']" @click="pickInProgressColor = !pickInProgressColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color['in progress']" @input="changeInProgressColor" v-if="pickInProgressColor"></chrome-picker>
            </v-flex>
            <v-flex xs12 sm6 class="mt-4 pr-4">
              <span class="pf-sub-head custom-title">Waiting text</span>
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.badgeText.waiting" label="Waiting badge text" class="mt-2"></v-text-field>
              <v-btn block :color="badgeOption.color.waiting" @click="pickWaitingColor = !pickWaitingColor, getCurrentBadge(i)"></v-btn>
              <chrome-picker :value="badgeOption.color.waiting" @input="changeWaitingColor" v-if="pickWaitingColor"></chrome-picker>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex xs12 sm6>
              <v-checkbox v-model="badgeOption.infoBlocks.show" label="Show infoblocks" class="pr-4" @change="isFormDataChanged = true"></v-checkbox>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>5. Show / Hide Data:</b>
              </h5>
              <span class="pf-sub-head mb-3 custom-title">Family:</span>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.religion" label="Show religion" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.waiting" label="Show waiting" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.education" label="Show education" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.ethnicity" label="Show ethnicity" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.age" label="Show age" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.gender" label="Show gender" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.countryState" label="Show country/state" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.noOfChildren" label="Show number of children" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.showFamilyVideo" label="Show family videos" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.family.socialWebsite" label="Show family website" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>6. Child Preferences:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.preference.ethnicity" label="Show ethnicity" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.preference.ageGroup" label="Show age group" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.dataOptions.preference.adoptionType" label="Show adoption type" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>7. Slider Badge Options:</b>
              </h5>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.sliderOptions.noOfFamilies" label="Total families to be displayed" class="mt-2"></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.sliderOptions.noOfSlides" label="Total number of slides" class="mt-2"></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.sliderOptions.showSliderDots" label="Show slider dots" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.sliderOptions.showSliderArrows" label="Show slider arrows" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6>
              <v-select v-model="badgeOption.sliderOptions.autoPlay" label="Auto play slider" :items="yesOrNoArray" class="pr-4" @change="isFormDataChanged = true"></v-select>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.sliderOptions.waitingFamiliesPage" label="Waiting families page URI" class="mt-2"></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pr-4">
              <v-text-field @input="isFormDataChanged = true" v-model="badgeOption.sliderOptions.waitingFamilyProfilePage" label="Waiting family profile page URI" class="mt-2"></v-text-field>
            </v-flex>
          </v-layout>

          <v-layout wrap class="pb-4 pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>8. Questions and Preferences:</b>
              </h5>
              <small>* The preferences to be asked to the visitors before entering to the badge.</small><br/>
              <small>* The visitors email address and contact number will be added by default.</small>
            </v-flex>
            <div class="d-flex align-center">
              <span class="mr-2 pf-sub-head custom-title">{{ typeof badgeOption.finderOptions.active === 'undefined' ? 'Disabled' : (badgeOption.finderOptions.active ? 'Enabled' : 'Disabled') }}</span>
              <v-switch value v-model="badgeOption.finderOptions.active" :input-value="typeof badgeOption.finderOptions.active === 'undefined' ? false : badgeOption.finderOptions.active" @change="isFormDataChanged = true"></v-switch>
            </div>
            <v-flex xs12 class="mb-3" v-if="badgeOption.finderOptions && badgeOption.finderOptions.active">
              <span class="pf-sub-head custom-title">Questions</span><br/>
              <v-layout wrap class="flex-column">
                <v-switch
                    v-model="selectAllQuestions"
                    @change="toggleSelectAllQuestions(i)"
                    label="Select All"
                    class="pr-4 pl-1 mt-2">
                </v-switch>
                <v-layout wrap class="row">
                  <!-- <v-flex pl-0 v-for="(question, q) in badgeOption.finderOptions.questions" :key="q" sm6> -->
                  <v-flex pl-0 v-for="(question, q) in questionLabels" :key="q" sm6>
                    <div>
                      <v-checkbox class="ma-0" :label="questionLabels[q]" v-model="badgeOption.finderOptions.questions[q]" color="teal" @change="isFormDataChanged = true; updateSelectAllQuestions(i);" :disabled="['name', 'contact'].indexOf(q) > -1"></v-checkbox>
                    </div>
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-flex>
          </v-layout>

          <!-- The badge codes -->
          <v-layout wrap class="pl-4">
            <v-flex class="xs12">
              <h5 class="pf-sub-head mb-3 custom-title">
                <b>9. Badge Code:</b>
              </h5>
            </v-flex>
            <!-- The list badge code -->
            <span class="pf-sub-head ml-2 mb-3 custom-title">{{ badgeCode.copyListCodeInfo }}</span>
            <v-flex xs12 class="pr-4">
              <v-card class="pa-3">
                <v-layout>
                <v-flex xs10 sm11 class="code-label">
                  <v-label>
                    <div :class="[isListCodeSelected == true ? 'select-area pa-1' : 'pa-1']">{{ unescape(listBadgeCode(i + 1)) }}</div>
                  </v-label>
                </v-flex>
                <v-flex xs2 sm1 class="text-xs-right">
                  <v-btn icon fab class="ma-0"
                    v-clipboard:copy="unescape(listBadgeCode(i + 1))"
                    v-clipboard:success="onCopyListCode"
                    v-clipboard:error="onError">
                    <v-icon size="22" class="pr-1">file_copy</v-icon>
                  </v-btn>
                </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
            <v-flex xs12 class="pr-4 pl-4 clipboard-message">
              <v-label v-if="isListCodeSelected">Code copied to clipboard</v-label>
            </v-flex>

            <!-- The carousel badge code -->
            <span class="pf-sub-head ml-2 mb-3 custom-title">{{ badgeCode.copyCarouselCodeInfo }}</span>
            <v-flex xs12 class="pr-4">
              <v-card class="pa-3">
                <v-layout>
                <v-flex xs10 sm11 class="code-label">
                  <v-label>
                    <div :class="[isCarouselCodeSelected == true ? 'select-area pa-1' : 'pa-1']">{{ unescape(carouselBadgeCode(i + 1)) }}</div>
                  </v-label>
                </v-flex>
                <v-flex xs2 sm1 class="text-xs-right">
                  <v-btn icon fab class="ma-0"
                    v-clipboard:copy="unescape(carouselBadgeCode(i + 1))"
                    v-clipboard:success="onCopyCarouselCode"
                    v-clipboard:error="onError">
                    <v-icon size="22" class="pr-1">file_copy</v-icon>
                  </v-btn>
                </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
            <v-flex xs12 class="pr-4 pl-4 clipboard-message">
              <v-label v-if="isCarouselCodeSelected">Code copied to clipboard</v-label>
            </v-flex>
          </v-layout>
        </v-container>
        <v-layout class="button-container pr-4 pl-4"> 
          <v-flex xs12 sm12 md12 v-if="isFormDataChanged" class="pr-0 text-xs-right">
            <div class="d-inline-flex">
              <v-btn class="pf-btn pf-btn-cancel" @click="hideButtons">
                <v-icon size="16" class="pr-1">cancel</v-icon>
                <div>Cancel</div>
              </v-btn>
              <v-btn class="pf-btn pf-btn-save" @click="save">
                <v-icon size="16" class="pr-1">check_circle</v-icon>
                <div>Save</div>
              </v-btn>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </div>
</template>
<script>

/* global session store config */
import Vue from 'vue';
import VueClipboard from 'vue-clipboard2';
import { Chrome } from 'vue-color';
import swal from 'sweetalert2';
import { update } from '../../helpers/helpers';
Vue.use(VueClipboard);

// The non-wordpress badge setup page for the agency
export default {
  name: 'AgencyBadgeSetup',
  components: {
    'chrome-picker': Chrome,
  },
  data: vm => ({
    currentBadge: {},
    isListCodeSelected: false,
    isCarouselCodeSelected: false,
    isFormDataChanged: false,
    showPassword: false,
    sortingOptions: [
      { text: 'Alphabetic', value: 'first_name' },
      { text: 'Newest First', value: 'newest' },
      { text: 'Oldest Waiting', value: 'oldest' },
      { text: 'Random', value: 'random' },
      { text: 'Rotating', value: 'rotating' },
      { text: 'Youngest Waiting', value: 'youngest' }
    ],
    noOfFamilies: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
    noOfCols: [2, 3, 4],
    filteringOptions: [
      { text: 'Profiles I like', value: 'favorite' },
      { text: 'Kids in family', value: 'kidsInFamily' },
      { text: 'Religion', value: 'religion' },
      { text: 'Country/State', value: 'location' },
      { text: 'Waiting', value: 'waiting' },
      { text: 'Account type', value: 'accountType' },
      { text: 'Special needs', value: 'specialNeeds' },
      { text: 'Child ethnicity', value: 'childEthnicity' },
      { text: 'Openness', value: 'adoptionTypePref' },
      { text: 'Status', value: 'avatarLabel' },
      { text: 'Sort By', value: 'sort_by' }
    ],
    finderFilteringOptions: [
      { text: 'Kids in family', value: 'kidsInFamily' },
      { text: 'Religion', value: 'religion' },
      { text: 'Country/State', value: 'location' },
      { text: 'Waiting', value: 'waiting' },
      { text: 'Account type', value: 'accountType' },
      { text: 'Special needs', value: 'specialNeeds' },
      { text: 'Child ethnicity', value: 'childEthnicity' },
      { text: 'Openness', value: 'adoptionTypePref' },
      { text: 'Status', value: 'avatarLabel' }
    ],
    badgeCode: {
      copyListCodeInfo: 'Copy the code below and paste it on your website to list all your families',
      copyCarouselCodeInfo: 'Copy the code below and paste it on your website to show a carousel of choosen number of families',
    },
    pickPrimaryColor: false,
    pickSecondaryColor: false,
    pickMatchedColor: false,
    pickPlacedColor: false,
    pickInProgressColor: false,
    pickWaitingColor: false,
    pickTopbarColor: false,
    pickTopbarTextColor: false,
    yesOrNoArray: [
      { text: 'Yes', value: true },
      { text: 'No', value: false }
    ],
    defaultBadgeOptions: {},
    defaultSortingOptions: [],
    badgeOptions: [{
      finderOptions: {
        active: false,
        questions: {
          name: true,
          location: true,
          currentSituation: true,
          // dueDate: true,
          // childAge: true,
          childEthnicity: true,
          fatherEthnicity: true,
          alcohol: true,
          drug: true,
          accountType: true,
          openToNearest: true,
          kidsInFamily: false,
          religion: true,
          waiting: false,
          specialNeeds: false,
          adoptionTypePref: false,
          avatarLabel: false,
          callback: true,
          contact: true
        },
      },
      generalOptions: {
        numberOfFamilies: 12,
        colsPerRow: 4,
        sortingOptions: ['random'],
        filteringOptions: ['favorite', 'kidsInFamily', 'religion', 'location', 'waiting', 'avatarLabel'],
        defaultSorting: {
          item: 'None',
          value: ''
        },
        disableInprogressProfiles: false,
        kidsInFamilyOption: 0,
        showCountryState: 'both',
        fontFamily: 'theme',
        profilePassword: ''
      },
      color: {
        'primary': '#7AC142',
        'secondary': '#F195BF',
        'topbar': '#454d5a',
        'topbarText': '#FFFFFF',
        'matched': '#63ACDE',
        'placed': '#019D8C',
        'in progress': '#828282',
        'waiting': '#7b2121'
      },
      badgeText: {
        'in progress': 'In Progress',
        'matched': 'Matched',
        'placed': 'Placed',
        'waiting': 'Waiting'
      },
      infoBlocks: {
        show: false,
        position: 7,
        data: []
      },
      dataOptions: {
        family: {
          religion: true,
          waiting: true,
          education: true,
          ethnicity: true,
          age: false,
          gender: false,
          countryState: true,
          noOfChildren: false,
          socialWebsite: true
        },
        preference: {
          ethnicity: true,
          ageGroup: true,
          adoptionType: false
        }
      },
      sliderOptions: {
        noOfFamilies: 12,
        noOfSlides: 3,
        showSliderDots: true,
        showSliderArrows: false,
        autoPlay: true,
        waitingFamiliesPage: '',
        waitingFamilyProfilePage: 'view'
      }
    }],
    selectAllQuestions: false,
    questions: {
      name: true,
      location: true,
      currentSituation: true,
      // dueDate: true,
      // childAge: true,
      childEthnicity: true,
      fatherEthnicity: true,
      alcohol: true,
      drug: true,
      accountType: true,
      openToNearest: true,
      kidsInFamily: false,
      religion: true,
      waiting: false,
      specialNeeds: false,
      adoptionTypePref: false,
      avatarLabel: false,
      callback: true,
      contact: true
    },
    questionLabels: {
      name: 'What is your name?',
      location: 'Where do you live?',
      currentSituation: 'What is your current situation?',
      // dueDate: 'When is your due date / child’s date of birth?',
      // childAge: 'How old is your child?',
      childEthnicity: 'What race are you looking for?',
      fatherEthnicity: 'What is the biological father’s race?',
      alcohol: 'Have you consumed any alcohol?',
      drug: 'Have you taken any drugs (legal or illegal) during pregnancy?',
      accountType: 'What type of families are you open to?',
      openToNearest: 'Are you open to families near to you?',
      kidsInFamily: 'Do you want to search families by number of their children?',
      religion: 'Do you have a religious preference?',
      waiting: 'Select if you want to filter families by their waiting time',
      specialNeeds: 'Do you want to see the families who are looking for children with special needs?',
      adoptionTypePref: 'What type of adoption you\'re looking for?',
      avatarLabel: 'Select the placement status of adoptive family',
      callback: 'Would you like to speak with a counselor?',
      contact: 'Please share your email address and phone number'
    },
  }),
  computed: {
    Agency: {
      get () {
        return store.state.Agency;
      },
      set (value) {
        store.commit('setAgency', value);
      }
    }
  },
  created () {
    this.defaultBadgeOptions = JSON.parse(JSON.stringify(this.badgeOptions[0]));
    if (this.Agency) {
      if (this.Agency.badge_options[0].dataOptions !== undefined) {
        this.badgeOptions = JSON.parse(JSON.stringify(this.Agency.badge_options));
      }
      // Set the finder options if not already there - later feature
      for (let i = 0; i < this.badgeOptions.length; i++) {
        if (this.badgeOptions[i].finderOptions === undefined) {
          this.badgeOptions[i].finderOptions = JSON.parse(JSON.stringify(this.defaultBadgeOptions.finderOptions));
        }
        if (this.badgeOptions[i].finderOptions.questions === undefined) {
          this.badgeOptions[i].finderOptions.questions = JSON.parse(JSON.stringify(this.defaultBadgeOptions.finderOptions.questions));
        }
        this.updateSelectAllQuestions(i);
        /* Add the topbar background and text color if not there - added later  */
        if (this.badgeOptions[i].color.topbar === undefined) {
          this.badgeOptions[i].color.topbar = '#454d5a';
        }
        if (this.badgeOptions[i].color.topbarText === undefined) {
          this.badgeOptions[i].color.topbarText = '#ffffff';
        }
        /* Set the default sorting */
        if (typeof this.badgeOptions[i].generalOptions.defaultSorting === 'undefined') {
          this.badgeOptions[i].generalOptions.defaultSorting = {
            item: 'None',
            value: ''
          };
        }
        /* Set the show video if not set */
        if (typeof this.badgeOptions[i].generalOptions.showVideo === 'undefined') {
          this.badgeOptions[i].generalOptions.showVideo = true;
        }
        /* Set the show family video if not set */
        if (typeof this.badgeOptions[i].dataOptions.family.showFamilyVideo === 'undefined') {
          this.badgeOptions[i].dataOptions.family.showFamilyVideo = true;
        }

        /* Set the show family video if not set */
        if (typeof this.badgeOptions[i].sliderOptions.waitingFamilyProfilePage === 'undefined') {
          this.badgeOptions[i].sliderOptions.waitingFamilyProfilePage = 'view';
        }
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    if (!session.isOnline()) {
      next({ path: '/login' });
    } else {
      next();
    }
  },
  methods: {
    toggleSelectAllQuestions (i) {
      this.isFormDataChanged = true;
      Object.keys(this.badgeOptions[i].finderOptions.questions).forEach(x => {
        if (['name', 'contact'].indexOf(x) === -1) {
          this.badgeOptions[i].finderOptions.questions[x] = this.selectAllQuestions;
        }
      });
    },
    updateSelectAllQuestions (i) {
      // Set selectAllQuestions if all checked
      let trueCount = 0;
      Object.keys(this.badgeOptions[i].finderOptions.questions).forEach(x => {
        if (['name', 'contact'].indexOf(x) === -1) {
          if (this.badgeOptions[i].finderOptions.questions[x]) trueCount++;          
        }
      });
      if (Object.keys(this.badgeOptions[i].finderOptions.questions).filter(x => ['name', 'contact'].indexOf(x) === -1).length === trueCount) {
        this.selectAllQuestions = true;
      } else {
        this.selectAllQuestions = false;
      }
    },
    getDefaultSortingItems (currentBadge) {
      // Enabled only location now
      let defaultSortingItems = [...['None'], ...this.filteringOptions.filter(x => ['location'].indexOf(x.value) > -1).filter(y => currentBadge.generalOptions.filteringOptions.indexOf(y.value) > -1)];
      let locationIndex = defaultSortingItems.findIndex(x => x.value === 'location');
      if (locationIndex > -1) {
        defaultSortingItems.splice(locationIndex, 1);
        defaultSortingItems.push(
          { text: 'Country', value: 'country' },
          { text: 'State', value: 'state' }
        );
      }
      return defaultSortingItems;
      // [...['None'], ...this.filteringOptions.filter(x => ['favorite', 'sort_by'].indexOf(x.value) === -1).filter(y => currentBadge.generalOptions.filteringOptions.indexOf(y.value) > -1)];
    },
    getDefaultSortingOptions (currentBadge) {
      this.isFormDataChanged = true;
      currentBadge.generalOptions.defaultSorting.value = '';
      this.defaultSortingOptions = [];
      if (currentBadge.generalOptions.defaultSorting.item !== 'None') {
        switch (currentBadge.generalOptions.defaultSorting.item) {

          case 'kidsInFamily':
            break;
          case 'religion':
            break;
          case 'country':
            this.defaultSortingOptions = this.getAvailableCountries();
            break;
          case 'state':
            this.defaultSortingOptions = this.getAvailableStates();
            break;
          case 'waiting':
            break;
          case 'specialNeeds':
            break;
          case 'childEthnicity':
            break;
          case 'adoptionTypePref':
            break;
          case 'avatarLabel':
            break;
          case 'sort_by':
            break;
          default:
            break;

        }
      }
    },
    getAvailableCountries () {
      let summaryField = this.Agency.is_super_admin ? 'summary_all' : 'summary';
      let agencyLocations = this.Agency[summaryField] ? this.Agency[summaryField].location : [];
      return Array.from(agencyLocations, x => x.value).sort();
    },
    getAvailableStates () {
      let summaryField = this.Agency.is_super_admin ? 'summary_all' : 'summary';
      let agencyLocations = this.Agency[summaryField] ? this.Agency[summaryField].location : [];
      let states = [];
      agencyLocations.forEach(c => {
        if (c.items) {
          c.items.forEach(s => {
            states.push(s.value);
          });
        }
      });
      return states.sort();
    },
    // @vuese
    // Used to hide the form buttons and set the agency badge obtions
    hideButtons () {
      this.isFormDataChanged = false;
      if (this.Agency && this.Agency.badge_options && this.Agency.badge_options.length && Object.keys(this.Agency.badge_options[0]).length > 0) {
        this.badgeOptions = JSON.parse(JSON.stringify(this.Agency.badge_options));
      } else {
        this.badgeOptions = [JSON.parse(JSON.stringify(this.defaultBadgeOptions))];
      }
    },
    // @vuese
    // Used to change the 'primary' color of the badge
    // @arg the new color code
    changePrimaryColor (newColor) {
      this.badgeOptions[this.currentBadge].color.primary = newColor.hex;
      this.isFormDataChanged = true;
    },
    // @vuese
    // Used to change the 'secondary' color of the badge
    // @arg the new color code
    changeSecondaryColor (newColor) {
      this.badgeOptions[this.currentBadge].color.secondary = newColor.hex;
      this.isFormDataChanged = true;
    },
    changeTopbarColor (newColor) {
      this.badgeOptions[this.currentBadge].color.topbar = newColor.hex;
      this.isFormDataChanged = true;
    },
    changeTopbarTextColor (newColor) {
      this.badgeOptions[this.currentBadge].color.topbarText = newColor.hex;
      this.isFormDataChanged = true;
    },
    // @vuese
    // Used to change the 'matched' color of the badge
    // @arg the new color code
    changeMatchedColor (newColor) {
      this.badgeOptions[this.currentBadge].color.matched = newColor.hex;
      this.isFormDataChanged = true;
    },
    // @vuese
    // Used to change the 'placed' color of the badge
    // @arg the new color code
    changePlacedColor (newColor) {
      this.badgeOptions[this.currentBadge].color.placed = newColor.hex;
      this.isFormDataChanged = true;
    },
    // @vuese
    // Used to change the 'in progress' color of the badge
    // @arg the new color code
    changeInProgressColor (newColor) {
      this.badgeOptions[this.currentBadge].color['in progress'] = newColor.hex;
      this.isFormDataChanged = true;
    },
    // @vuese
    // Used to change the 'waiting' color of the badge
    // @arg the new color code
    changeWaitingColor (newColor) {
      this.badgeOptions[this.currentBadge].color.waiting = newColor.hex;
      this.isFormDataChanged = true;
    },
    listBadgeCode (badgeNumber) {
      return '&lt;!-- List Badge Start --&gt;&lt;div id=&quot;pf-badge-families&quot; agency-id=&quot;' + this.Agency.id + '&quot; badge-type=&quot;list&quot; badge=&quot;' + badgeNumber + '&quot;&gt;&lt;/div&gt;&lt;script type=&quot;text/javascript&quot; src=&quot;' + config.uploadPath + 'badge/' + (process.env.NODE_ENV !== 'production' ? 'test' : 'production') + '/badge.js&quot; defer &gt;&lt;/script&gt;&lt;!-- List Badge End --&gt;';
    },
    carouselBadgeCode (badgeNumber) {
      return '&lt;!-- Carousel Badge Start --&gt;&lt;div id=&quot;pf-badge-families&quot; agency-id=&quot;' + this.Agency.id + '&quot; badge-type=&quot;carousel&quot; badge=&quot;' + badgeNumber + '&quot;&gt;&lt;/div&gt;&lt;script type=&quot;text/javascript&quot; src=&quot;' + config.uploadPath + 'badge/' + (process.env.NODE_ENV !== 'production' ? 'test-' : '') + 'carousel/badge.js&quot; defer &gt;&lt;/script&gt;&lt;!-- Carousel Badge End --&gt;';
    },
    // @vuese
    // Used to save the agency badge settings
    async save () {
      this.isFormDataChanged = false;
      this.Agency.badge_options = {}; // Added since the non used fields were not cleared on changing the field names
      this.Agency.badge_options = this.badgeOptions;
      this.Agency.updated_from = 'badge';
      let { error, data, status } = await update('Agency', this.Agency, session.getSession().adminUser ? session.getSession().user.human.agency : 'Me');
      if (error) {
        console.log('error', error);
        this.isFormDataChanged = true;
        this.snackbar = {
          show: true,
          color: 'red',
          text: 'Something went wrong, please try again.'
        };
      } else {
        this.Agency = data;
        swal.fire({
          type: 'success',
          title: 'Badge settings updated successfully',
          showConfirmButton: false,
          timer: 2500
        });
      }
      this.pickPrimaryColor = this.pickSecondaryColor = this.pickMatchedColor = this.pickPlacedColor = this.pickInProgressColor = this.pickWaitingColor = false;
    },
    // @vuese
    // Used to unescape the html string
    unescape (html) {
      let e = document.createElement('textarea');
      e.innerHTML = html;
      return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
    },
    // @vuese
    // Used to make the list badge text selected on copying.
    onCopyListCode: function (e) {
      this.isListCodeSelected = true;
    },
    // @vuese
    // Used to make the carousel badge text selected on copying.
    onCopyCarouselCode: function (e) {
      this.isCarouselCodeSelected = true;
    },
    // @vuese
    // Called on error while copying the badge text
    onError: function (e) {
      // alert('Failed to copy texts');
    },
    addBadge () {
      // Add new badge options
      this.badgeOptions.push(this.defaultBadgeOptions);
      this.isFormDataChanged = true;
      let newBadge = '#badge' + this.badgeOptions.length;
      setTimeout(() => {
        this.$vuetify.goTo(newBadge, { offset: 50 });
      }, 500);
    },
    removeBadge () {
      // Remove last badge options
      this.badgeOptions.pop();
      this.isFormDataChanged = true;
      setTimeout(() => {
        this.$vuetify.goTo('#badge1', { offset: 50 });
      }, 500);
    },
    getCurrentBadge (badgeOption) {
      this.currentBadge = badgeOption;
    }
  }
};
</script>

<style lang="css" scoped>
  .code-label{
    padding-top: 8px;
    overflow-y: scroll;
  }
  .code-label::-webkit-scrollbar, .select-area::-webkit-scrollbar{ 
    width: 0 !important 
  }
  .code-label, .code-label { 
    overflow: -moz-scrollbars-none; 
  }
  .code-label, .code-label { 
    -ms-overflow-style: none; 
  }
  .select-area{
    background-color: #99b3ff;
    color: #000;
    overflow-y: scroll;
  }
  .clipboard-message{
    min-height: 37px;
  }
  .questionCard {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Badge/setup.vue